import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import type { CreditTierSE } from '@/api';
import { formatInterestRate } from '@/localization/percentages';
import { selectMaxCreditTier } from '@/queries/creditTiersQuery/creditTiersQuerySelectors';
type Props = { creditTiers: CreditTierSE[] };

const CreditTierSummaryContent = ({ creditTiers }: Props) => {
  const { t } = useTranslation(['CreditTierSummary', 'Common']);

  const maxCreditTier = selectMaxCreditTier(creditTiers);

  return (
    <>
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-x-2 gap-y-0.5 [&>span:nth-child(4n)]:text-right">
        <span className="text-caption-semibold">{t('creditLimit')}</span>
        <span className="text-caption-semibold">{t('interestRate')}</span>
        <span className="text-caption-semibold text-right">{t('installmentAmount')}</span>
        <span className="text-caption-semibold">{t('totalCost')}</span>
        {creditTiers.map(creditTier => (
          <Fragment key={creditTier.id}>
            <span className="text-caption-regular text-text-subtle tabular-nums">
              {t('Common:money', { amount: creditTier.amount })}
            </span>
            <span className="text-caption-regular text-text-subtle tabular-nums">
              {formatInterestRate(creditTier.interest)}
            </span>
            <span className="text-caption-regular text-text-subtle text-right tabular-nums">
              {t('Common:money', { amount: creditTier.monthlyPayment })}
            </span>
            <span className="text-caption-regular text-text-subtle tabular-nums">
              {t('Common:money', { amount: creditTier.totalAmount })}
            </span>
          </Fragment>
        ))}
      </div>
      {!!maxCreditTier?.fees && (
        <div className="grid grid-cols-2 gap-0.5 [&>span:nth-child(2n+1):not(:first-child)]:text-right">
          <span className="text-caption-semibold col-span-2 mt-4">{t('fees.title')}</span>
          <span className="text-caption-regular text-text-subtle">{t('fees.setupFee')}</span>
          <span className="text-caption-regular text-text-subtle">
            {t('fees.upTo', { amount: maxCreditTier.fees.setupFee })}
          </span>
          <span className="text-caption-regular text-text-subtle">{t('fees.withdrawalFee')}</span>
          <span className="text-caption-regular text-text-subtle">
            {t('Common:money', { amount: maxCreditTier.fees.withdrawalFee })}
          </span>
          <span className="text-caption-regular text-text-subtle">{t('fees.installmentFee')}</span>
          <span className="text-caption-regular text-text-subtle">
            {t('fees.upToPerMonth', { amount: maxCreditTier.fees.installmentFee })}
          </span>
          <span className="text-caption-regular text-text-subtle">{t('fees.directDebitMissingFee')}</span>
          <span className="text-caption-regular text-text-subtle">
            {t('fees.perMonth', { amount: maxCreditTier.fees.directDebitMissingFee })}
          </span>
        </div>
      )}
    </>
  );
};

export default CreditTierSummaryContent;
